/**
 * Progress layer contract (ISSUE-067 / ADR-0020).
 *
 * Browser-safe Zod only — no SDK imports.
 *
 * D-07 law: no streaks, binary days, Monday reset, additive-only,
 * behavior-only, never premium-gated.
 *
 * The milestone set is a FIXED ENUM — there is deliberately no admin knob to
 * add an outcome-based one. All copy must pass the ALERT_BANNED_WORDS guard;
 * a unit test in packages/shared/src/schemas/__tests__/progress.test.ts
 * iterates the list at build time so unsafe copy fails CI.
 */
import { z } from 'zod';
/**
 * Full set of ~10 behavior-only milestones approved in ADR-0020 §3.
 * Order determines display priority (first is shown first).
 * NEVER add outcome-based milestones (kcal, weight, deficit, target-adherence).
 */
export const MILESTONES = [
    {
        id: 'first_log',
        name: 'First log',
        description: 'Logged your first meal.',
    },
    {
        id: 'first_photo_log',
        name: 'First photo log',
        description: 'Logged a meal with a photo.',
        // NOTE: awarding requires an is_photo_log indicator on meal_entry; parked
        // until the photo-capture column is added (ISSUE-068 follow-up).
    },
    {
        id: 'first_weekly_review_read',
        name: 'First weekly review',
        description: 'Read your first weekly summary.',
    },
    {
        id: 'first_week_5days',
        name: 'Five-day week',
        description: 'Logged on 5 or more days in a week.',
    },
    {
        id: 'logs_25',
        name: '25 logs',
        description: 'Reached 25 lifetime logs.',
    },
    {
        id: 'logs_100',
        name: '100 logs',
        description: 'Reached 100 lifetime logs.',
    },
    {
        id: 'logs_500',
        name: '500 logs',
        description: 'Reached 500 lifetime logs.',
    },
    {
        id: 'first_correction',
        name: 'First correction',
        description: 'Taught the app a correction for a food entry.',
    },
    {
        id: 'first_export',
        name: 'First export',
        description: 'Exported your data for the first time.',
    },
    {
        id: 'first_month',
        name: 'First month',
        description: 'Logged at least once in four consecutive weeks.',
    },
];
export const milestoneIdSchema = z.enum([
    'first_log',
    'first_photo_log',
    'first_weekly_review_read',
    'first_week_5days',
    'logs_25',
    'logs_100',
    'logs_500',
    'first_correction',
    'first_export',
    'first_month',
]);
export const milestoneSchema = z.object({
    id: milestoneIdSchema,
    name: z.string(),
    description: z.string(),
});
export const milestoneAwardSchema = z.object({
    milestoneId: milestoneIdSchema,
    awardedAt: z.string().datetime(),
});
// ── Week ring ──────────────────────────────────────────────────────────────────
/**
 * The seven-day progress ring for the current week (ADR-0020 §1).
 * `days[0]` = Monday, `days[6]` = Sunday (ISO 8601 week order).
 * A day is `true` when the user has ≥1 resolved entry on that calendar date.
 * Derived from the ledger on read — no stored state.
 */
export const weekProgressSchema = z.object({
    weekStartDate: z.string().date(), // YYYY-MM-DD (Monday)
    days: z.tuple([
        z.boolean(),
        z.boolean(),
        z.boolean(),
        z.boolean(),
        z.boolean(),
        z.boolean(),
        z.boolean(),
    ]),
});
// ── Weekly win chips ───────────────────────────────────────────────────────────
/**
 * A single win chip shown in the Monday weekly review (ADR-0020 §2).
 * `id` is a stable key for deduplication on the client.
 * `text` is the copy shown to the user; must pass the banned-words guard.
 */
export const weeklyWinSchema = z.object({
    id: z.string(),
    text: z.string().max(120),
});
// ── GET /me/progress/week response ─────────────────────────────────────────────
/**
 * Full response for GET /me/progress/week.
 *
 * When `enabled` is false (layer turned off, or flaggedEd default):
 *   returns { enabled: false } — no ring data, no awards written.
 * When `enabled` is true:
 *   returns { enabled: true, ...weekProgressSchema fields }
 */
export const weekProgressResponseSchema = z.discriminatedUnion('enabled', [
    z.object({ enabled: z.literal(false) }),
    weekProgressSchema.extend({ enabled: z.literal(true) }),
]);
// ── GET /me/progress (combined) ───────────────────────────────────────────────
/**
 * Combined progress response for GET /me/progress (ISSUE-067 / T4.1).
 * Consumed by T4.2 (home screen + insights).
 *
 * When enabled is false: no ring data, no milestones, no weekly win.
 * When enabled is true: full ring + awarded milestones + top weekly win (if any).
 *
 * latestWeeklyWin: the most notable win from the most recently generated weekly
 * summary; null when no weekly summary exists yet or has no wins. Populated from
 * the summary payload — not recomputed here (ADR-0020 §2, natural home = summary).
 */
export const progressResponseSchema = z.discriminatedUnion('enabled', [
    z.object({ enabled: z.literal(false) }),
    weekProgressSchema.extend({
        enabled: z.literal(true),
        milestones: z.array(milestoneAwardSchema),
        latestWeeklyWin: weeklyWinSchema.nullable(),
    }),
]);
// ── Activity calendar (T13.1 / ISSUE-073) ────────────────────────────────────
/**
 * One day slot in the activity calendar.
 * `logged` is true when the user has ≥1 resolved, non-deleted entry on that
 * UTC calendar date. No consecutive/streak field — D-07.
 */
export const activityCalendarDaySchema = z.object({
    date: z.string().date(), // YYYY-MM-DD
    logged: z.boolean(),
});
/**
 * GET /me/activity-calendar response.
 *
 * When enabled is false (layer off or flaggedEd default): no calendar data.
 * When enabled is true: sorted day array + additive count only.
 *
 * D-07: payload MUST NOT contain consecutive, streak, in_a_row, or any
 * gap-counting field — asserted in tests.
 */
export const activityCalendarResponseSchema = z.discriminatedUnion('enabled', [
    z.object({ enabled: z.literal(false) }),
    z.object({
        enabled: z.literal(true),
        from: z.string().date(),
        to: z.string().date(),
        days: z.array(activityCalendarDaySchema),
        totalLogged: z.number().int().nonnegative(),
    }),
]);
// ── Progress preference ────────────────────────────────────────────────────────
export const progressPreferenceSchema = z.object({
    /** Explicit user choice. null = use system default (true unless flaggedEd). */
    progressLayerEnabled: z.boolean().nullable(),
});
export const updateProgressPreferenceSchema = z.object({
    enabled: z.boolean(),
});
//# sourceMappingURL=progress.js.map